import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';

const SHOCKS = [
  { key: 's3', label: '-3%', pct: 0.03, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  { key: 's5', label: '-5%', pct: 0.05, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  { key: 's10', label: '-10%', pct: 0.10, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
];

export default function StressTestSummary({ batches, volatilitySettings }) {
  const stressData = useMemo(() => {
    const activeBatches = (batches || [])?.filter(b =>
      ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)
    );

    const metalMap = {};
    activeBatches?.forEach(b => {
      const metal = b?.metal_name || 'Sin Metal';
      const exposure = (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0);
      if (!metalMap?.[metal]) metalMap[metal] = { exposure: 0, weight: 0 };
      metalMap[metal].exposure += exposure;
      metalMap[metal].weight += parseFloat(b?.weight_t) || 0;
    });

    return Object.entries(metalMap)?.map(([metal, data]) => ({
        metal,
        exposure: data?.exposure,
        weight: data?.weight,
        losses: SHOCKS?.map(s => ({ ...s, loss: data?.exposure * s?.pct })),
      }))?.sort((a, b) => b?.exposure - a?.exposure);
  }, [batches, volatilitySettings]);

  const totalByShock = useMemo(() => {
    return SHOCKS?.map(s => ({
      ...s,
      total: stressData?.reduce((sum, m) => sum + m?.exposure * s?.pct, 0),
    }));
  }, [stressData]);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Zap" size={18} color="var(--color-error)" strokeWidth={2} />
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            <Tooltip
              title="Stress Test de Mercado"
              description="Simula el impacto económico de caídas de precio en los benchmarks de cada metal. Muestra la pérdida potencial en € para escenarios de -3%, -5% y -10%."
              example="Si la exposición en Cobre es 50.000 € y el precio cae un 5%, la pérdida potencial es 2.500 €"
            >
              Stress Test de Mercado
            </Tooltip>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {SHOCKS?.map(s => (
            <Tooltip
              key={s?.key}
              title={`Escenario ${s?.label}`}
              description={`Pérdida potencial si el benchmark de cada metal cae un ${Math.abs(s?.pct * 100)}% desde el precio de compra. Calculado sobre la exposición total de cada metal.`}
            >
              <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${s?.bg} ${s?.color}`}>{s?.label}</span>
            </Tooltip>
          ))}
        </div>
      </div>
      {/* Total row */}
      <div className="grid grid-cols-4 gap-0 border-b border-border bg-muted/30 px-4 py-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <Tooltip
            title="Total Cartera"
            description="Suma de la exposición de todos los metales activos en inventario. Las pérdidas potenciales se calculan aplicando el porcentaje de caída a este total."
          >
            Total Cartera
          </Tooltip>
        </div>
        {totalByShock?.map(s => (
          <div key={s?.key} className="text-right">
            <p className="text-xs text-muted-foreground">
              <Tooltip
                title={`Pérdida potencial ${s?.label}`}
                description={`Pérdida potencial en € si el benchmark del metal cae ${s?.label} desde el precio actual. Calculada sobre la exposición total ponderada por yield.`}
              >
                {s?.label}
              </Tooltip>
            </p>
            <p className={`text-sm font-mono font-bold ${s?.color}`}>-{s?.total?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</p>
          </div>
        ))}
      </div>
      {/* Per-metal rows */}
      <div className="divide-y divide-border/50">
        {stressData?.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Sin lotes activos para analizar
          </div>
        ) : (
          stressData?.map(row => (
            <div key={row?.metal} className="grid grid-cols-4 gap-0 px-4 py-3 hover:bg-muted/20 transition-colors">
              <div>
                <p className="text-sm font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{row?.metal}</p>
                <Tooltip
                  title={`Exposición en ${row?.metal}`}
                  description="Capital total comprometido en lotes activos de este metal. Base de cálculo para los escenarios de stress."
                >
                  <p className="text-xs text-muted-foreground font-mono">{row?.exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} € exp.</p>
                </Tooltip>
              </div>
              {row?.losses?.map(l => (
                <div key={l?.key} className="text-right">
                  <Tooltip
                    title={`Pérdida ${l?.label} — ${row?.metal}`}
                    description={`Si el precio de ${row?.metal} cae un ${Math.abs(l?.pct * 100)}%, la pérdida estimada sobre la exposición de ${row?.exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} € sería de ${l?.loss?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €.`}
                  >
                    <p className={`text-sm font-mono font-semibold ${l?.color}`}>-{l?.loss?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</p>
                  </Tooltip>
                  <p className="text-xs text-muted-foreground">{l?.label}</p>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
